import React from 'react'

import {
  Body2,
  Box,
  Display,
  Heading2,
  JustifyContent,
  Link,
  Space,
} from '@gousto-internal/citrus-react'
import { BenefitItem, LandingPageButton, LargeImageWithText } from '../../components'
import type { HowGoustoFoodBoxesWorkTypes } from '../../types'
import {
  HowGoustoFoodBoxesWorkSections,
  simpleTrackRedirect,
  TargetUri,
  TrackingActions,
} from '../../types/tracking'
import dinner from '../assets/dinner.jpg'
import dinnerDesktop from '../assets/dinnerDesktop.jpg'
import { WHY_GOUSTO } from './config'

export function WhyChooseGoustoSection({ startOnboarding }: HowGoustoFoodBoxesWorkTypes) {
  return (
    <LargeImageWithText altText={WHY_GOUSTO.altText} desktopSrc={dinnerDesktop} mobileSrc={dinner}>
      <Heading2>How is Gousto different from other food boxes?</Heading2>
      <Space size={3} />
      <Body2>Here&apos;s what sets us apart:</Body2>
      <Space size={6} />

      <BenefitItem title="More choice">
        With over 175 recipes to choose from every week, we&apos;ve got more options than other food
        boxes - from{' '}
        <LandingPageButton
          asLink
          startOnboarding={startOnboarding}
          sectionName={HowGoustoFoodBoxesWorkSections.WHY_GOUSTO_SECTION}
          target={TargetUri.TEN_MINUTE_MEALS}
          action={TrackingActions.SPEEDY_10_MIN_MEALS_HELP}
        >
          10-Minute Meals
        </LandingPageButton>{' '}
        to{' '}
        <LandingPageButton
          startOnboarding={startOnboarding}
          asLink
          sectionName={HowGoustoFoodBoxesWorkSections.WHY_GOUSTO_SECTION}
          target={TargetUri.FAMILY}
          action={TrackingActions.EVERYDAY_FAVOURITES_HELP}
        >
          Family Favourites
        </LandingPageButton>{' '}
        and easy indulgence.
      </BenefitItem>

      <BenefitItem title="Make it work for you">
        Choose how many recipes you want, how many people you&apos;re cooking for (1 to 5), and how
        often. Whether it&apos;s weekly, fortnightly or monthly.
      </BenefitItem>

      <BenefitItem title="Ingredient quality">
        We partner with{' '}
        <Link
          href={TargetUri.SUPPLIERS}
          onClick={() =>
            simpleTrackRedirect(
              TrackingActions.SUPPLIERS,
              HowGoustoFoodBoxesWorkSections.WHY_GOUSTO_SECTION,
              TargetUri.SUPPLIERS,
            )
          }
        >
          trusted suppliers
        </Link>{' '}
        for 100% British meat and responsibly sourced ingredients.
      </BenefitItem>

      <BenefitItem title="Sustainability-focused">
        Our Eco Chill Box uses recycled paper, and we cut food waste wherever possible by giving you
        exactly what you need.
      </BenefitItem>

      <BenefitItem title="Fuss-free dinners">
        Pre-measured ingredients and step-by-step recipes take the guesswork out of dinner, saving
        time in the kitchen - and at the shop.
      </BenefitItem>

      <BenefitItem title="Family-friendly and healthier options">
        Go low-calorie, big on protein, vegetarian or gluten-free with our weekly changing menu.
        Plus, we have family-friendly recipes that&apos;ll please the crowd.
      </BenefitItem>

      <BenefitItem title="Better value for money">
        With meals starting at{' '}
        <LandingPageButton
          startOnboarding={startOnboarding}
          asLink
          sectionName={HowGoustoFoodBoxesWorkSections.WHY_GOUSTO_SECTION}
          target={TargetUri.BOX_PRICES}
          action={TrackingActions.BOX_PRICING_NAVIGATION_CLICKED}
        >
          £3.09 per serving,
        </LandingPageButton>{' '}
        you get flavour-packed dishes delivered at a fraction of takeaway prices.
      </BenefitItem>

      <Space size={6} />
      <Box
        display={Display.Flex}
        justifyContent={[JustifyContent.Center, JustifyContent.FlexStart]}
      >
        <LandingPageButton
          startOnboarding={startOnboarding}
          sectionName={HowGoustoFoodBoxesWorkSections.WHY_GOUSTO_SECTION}
          target={TargetUri.SIGNUP}
          action={TrackingActions.CLICK_GET_STARTED}
        >
          {WHY_GOUSTO.ctaText}
        </LandingPageButton>
      </Box>
    </LargeImageWithText>
  )
}
